/**
 * Centralized Defaults Configuration for Site DNA
 * 
 * This file contains ALL default values used throughout the application.
 * 
 * IMPORTANT RULES:
 * 1. Every default MUST have a documented source
 * 2. Components MUST show visual indicators when defaults are in use
 * 3. Critical financial values should BLOCK analysis rather than silently default
 * 
 * Categories:
 * - FINANCIAL: Financial assumptions for NPV, payback, etc.
 * - EQUIPMENT_COST: Default pricing for PV, battery, generator
 * - SOFT_COSTS: Engineering, permits, project management percentages
 * - TARIFF: Default utility rate structures
 * - LOAD_MODEL: Load modeling parameters
 * - RESILIENCE: Outage simulation parameters
 * - TECHNICAL: System efficiency and degradation assumptions
 */

// =============================================================================
// FINANCIAL DEFAULTS
// =============================================================================

export const FINANCIAL_DEFAULTS = {
    analysisYears: {
        value: 20,
        unit: 'years',
        source: 'Industry standard for commercial solar+storage projects',
        description: 'Project analysis period for NPV and lifecycle calculations',
        userEditable: true,
    },
    discountRate: {
        value: 0.08,
        unit: 'fraction',
        source: 'NREL Annual Technology Baseline 2024 - Commercial weighted average cost of capital',
        description: 'Discount rate for present value calculations',
        userEditable: true,
    },
    utilityEscalation: {
        value: 0.03,
        unit: 'fraction/year',
        source: 'EIA Annual Energy Outlook 2024 - Average commercial electricity price escalation',
        description: 'Annual rate of utility cost increase',
        userEditable: true,
    },
    omEscalation: {
        value: 0.02,
        unit: 'fraction/year',
        source: 'Industry standard - inflation-linked O&M escalation',
        description: 'Annual rate of O&M cost increase',
        userEditable: true,
    },
    degradationRate: {
        value: 0.005,
        unit: 'fraction/year',
        source: 'NREL - Typical crystalline silicon PV degradation rate',
        description: 'Annual PV output degradation',
        userEditable: true,
    },
    taxRate: {
        value: 0.25,
        unit: 'fraction',
        source: 'Approximate blended federal/state corporate tax rate',
        description: 'Combined tax rate for incentive calculations',
        userEditable: true,
    },
    itcRate: {
        value: 0.30,
        unit: 'fraction',
        source: 'Inflation Reduction Act 2024 - Base ITC rate for solar+storage',
        description: 'Investment Tax Credit percentage',
        userEditable: true,
    },
};

// =============================================================================
// EQUIPMENT COST DEFAULTS
// These are used ONLY when:
// 1. API fetch for admin-managed rates fails
// 2. User has not entered custom values
// =============================================================================

export const EQUIPMENT_COST_DEFAULTS = {
    pvTurnkey: {
        value: 1800,
        unit: '$/kW',
        source: 'NREL ATB 2024 - Commercial rooftop PV installed cost',
        description: 'Turnkey PV system cost including installation',
        userEditable: true,
        apiManaged: true, // Indicates this can be overridden by admin API
    },
    batteryEquipment: {
        value: 350,
        unit: '$/kWh',
        source: 'NREL ATB 2024 - Utility-scale battery storage',
        description: 'Battery equipment cost per kWh capacity',
        userEditable: true,
        apiManaged: true,
    },
    batteryPower: {
        value: 200,
        unit: '$/kW',
        source: 'NREL ATB 2024 - Battery power electronics',
        description: 'Battery inverter/power electronics cost',
        userEditable: true,
        apiManaged: true,
    },
    batteryInstall: {
        value: 100,
        unit: '$/kWh',
        source: 'Industry average - battery installation labor',
        description: 'Battery installation cost per kWh',
        userEditable: true,
        apiManaged: true,
    },
    generatorEquipment: {
        value: 800,
        unit: '$/kW',
        source: 'Industry average - diesel genset equipment',
        description: 'Generator equipment cost per kW',
        userEditable: true,
        apiManaged: true,
    },
    generatorInstall: {
        value: 200,
        unit: '$/kW',
        source: 'Industry average - generator installation',
        description: 'Generator installation cost per kW',
        userEditable: true,
        apiManaged: true,
    },
    switchgear: {
        value: 50000,
        unit: '$/system',
        source: 'Industry average - medium voltage switchgear',
        description: 'Switchgear cost per system',
        userEditable: true,
        apiManaged: true,
    },
};

// =============================================================================
// SOFT COSTS DEFAULTS
// =============================================================================

export const SOFT_COST_DEFAULTS = {
    engineering: {
        value: 5,
        unit: '%',
        source: 'NREL benchmark - engineering design percentage',
        description: 'Engineering and design costs as percentage of equipment',
        userEditable: true,
    },
    permits: {
        value: 3,
        unit: '%',
        source: 'NREL benchmark - permitting and interconnection',
        description: 'Permit and interconnection costs',
        userEditable: true,
    },
    projectManagement: {
        value: 4,
        unit: '%',
        source: 'Industry standard - project management overhead',
        description: 'Project management costs',
        userEditable: true,
    },
    insurance: {
        value: 1,
        unit: '%',
        source: 'Industry standard - construction insurance',
        description: 'Builder\'s risk insurance',
        userEditable: true,
    },
    contingency: {
        value: 5,
        unit: '%',
        source: 'Industry standard - project contingency',
        description: 'Contingency reserve',
        userEditable: true,
    },
    profit: {
        value: 8,
        unit: '%',
        source: 'Industry standard - developer profit margin',
        description: 'Developer/contractor profit margin',
        userEditable: true,
    },
};

// =============================================================================
// TARIFF DEFAULTS
// WARNING: These should NOT be used for financial analysis!
// They exist only for display/preview purposes when no tariff is selected.
// =============================================================================

export const TARIFF_DEFAULTS = {
    blendedEnergyRate: {
        value: 0.12,
        unit: '$/kWh',
        source: 'EIA - US average commercial electricity rate 2024',
        description: 'Generic blended energy rate (NOT for analysis)',
        userEditable: true,
        warningWhenUsed: 'This is a generic national average. Select a specific tariff for accurate analysis.',
    },
    blendedDemandRate: {
        value: 15.0,
        unit: '$/kW',
        source: 'Industry average - commercial demand charge',
        description: 'Generic demand charge rate',
        userEditable: true,
        warningWhenUsed: 'This is a generic estimate. Select a specific tariff for accurate analysis.',
    },
    monthlyFixedCharge: {
        value: 25.0,
        unit: '$/month',
        source: 'Industry average - commercial fixed charge',
        description: 'Monthly fixed service charge',
        userEditable: true,
    },
};

// =============================================================================
// LOAD MODEL DEFAULTS
// =============================================================================

export const LOAD_MODEL_DEFAULTS = {
    evCharging: {
        fleetSize: {
            value: 10,
            unit: 'vehicles',
            source: 'Small commercial fleet starting point',
            description: 'Number of EVs in fleet',
        },
        numChargers: {
            value: 5,
            unit: 'chargers',
            source: 'Typical 2:1 vehicle-to-charger ratio',
            description: 'Number of charging stations',
        },
        chargerPower: {
            value: 7.2,
            unit: 'kW',
            source: 'Standard Level 2 charger power',
            description: 'Power per charger',
        },
        dailyMiles: {
            value: 30,
            unit: 'miles/vehicle/day',
            source: 'Average US commute distance',
            description: 'Daily driving distance per vehicle',
        },
    },
    enrichment: {
        availability: {
            value: 0.96,
            unit: 'fraction',
            source: 'Typical industrial process availability',
            description: 'Process availability factor',
        },
        hvacFraction: {
            value: 0.3,
            unit: 'fraction',
            source: 'Typical HVAC load as fraction of process load',
            description: 'HVAC as fraction of running load',
        },
        hvacSeasonalAmplitude: {
            value: 0.1,
            unit: 'fraction',
            source: 'Moderate climate seasonal variation',
            description: 'HVAC seasonal amplitude',
        },
        auxLoadKw: {
            value: 100,
            unit: 'kW',
            source: 'Generic auxiliary load estimate',
            description: 'Constant auxiliary load',
        },
    },
    coldStorage: {
        kwPer1000Cf: {
            value: 15,
            unit: 'kW/1000 cf',
            source: 'ASHRAE cold storage benchmarks',
            description: 'Power per 1000 cubic feet of storage',
        },
        auxLoadKw: {
            value: 50,
            unit: 'kW',
            source: 'Typical auxiliary equipment load',
            description: 'Auxiliary systems load',
        },
    },
};

// =============================================================================
// RESILIENCE SIMULATION DEFAULTS
// =============================================================================

export const RESILIENCE_DEFAULTS = {
    outageStartHour: {
        value: 14,
        unit: 'hour (0-23)',
        source: 'Peak demand typically occurs in afternoon',
        description: 'Default outage start time for simulations',
        userEditable: true,
    },
    criticalLoadFraction: {
        value: 0.5,
        unit: 'fraction',
        source: 'Industry convention - 50% of load is critical',
        description: 'Fraction of load considered critical during outage',
        userEditable: true,
    },
    batteryReservePercent: {
        value: 20,
        unit: '%',
        source: 'Industry best practice - battery state of charge floor',
        description: 'Minimum battery state of charge',
        userEditable: true,
    },
};

// =============================================================================
// TECHNICAL DEFAULTS
// =============================================================================

export const TECHNICAL_DEFAULTS = {
    inverterEfficiency: {
        value: 0.96,
        unit: 'fraction',
        source: 'Modern grid-tied inverter efficiency',
        description: 'DC to AC conversion efficiency',
        userEditable: false,
    },
    batteryRoundTripEfficiency: {
        value: 0.92,
        unit: 'fraction',
        source: 'Li-ion battery round-trip efficiency',
        description: 'Battery charge/discharge efficiency',
        userEditable: false,
    },
    batteryOneWayEfficiency: {
        value: 0.96,
        unit: 'fraction',
        source: 'sqrt(0.92) - single direction efficiency',
        description: 'Battery single-direction efficiency',
        userEditable: false,
    },
    batteryCalendarFade: {
        value: 0.008,
        unit: 'fraction/year',
        source: 'Li-ion calendar aging rate',
        description: 'Annual capacity loss from calendar aging',
        userEditable: false,
    },
    batteryCycleFade: {
        value: 0.000015,
        unit: 'fraction/cycle',
        source: 'Li-ion cycle aging rate',
        description: 'Capacity loss per full cycle',
        userEditable: false,
    },
};

// =============================================================================
// GENERATOR DEFAULTS
// =============================================================================

export const GENERATOR_DEFAULTS = {
    fuelCostPerGallon: {
        value: 3.0,
        unit: '$/gallon',
        source: 'EIA - Average US diesel price',
        description: 'Diesel fuel cost',
        userEditable: true,
    },
    fuelCostPerMmbtu: {
        value: 6.0,
        unit: '$/MMBtu',
        source: 'EIA - Average US natural gas price',
        description: 'Natural gas fuel cost',
        userEditable: true,
    },
    gensetSizeKw: {
        value: 3000,
        unit: 'kW',
        source: 'Common industrial genset size',
        description: 'Default generator size',
        userEditable: true,
    },
    numGensets: {
        value: 4,
        unit: 'units',
        source: 'N+1 redundancy typical configuration',
        description: 'Number of generators',
        userEditable: true,
    },
    turndownPercent: {
        value: 15,
        unit: '%',
        source: 'Diesel generator minimum load recommendation',
        description: 'Minimum generator loading',
        userEditable: true,
    },
};

// =============================================================================
// VORTX SYSTEM CONSTANTS
// =============================================================================

// Vortx DC Bus Voltage (used for DC-coupled systems)
export const VORTX_DC_BUS_VOLTAGE = 700; // Volts

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a value matches its default
 * @param {string} category - Category name (e.g., 'financial', 'equipment')
 * @param {string} key - Key within the category
 * @param {any} currentValue - The current value to check
 * @returns {boolean} - True if the current value matches the default
 */
export function isUsingDefault(category, key, currentValue) {
    const defaults = getDefaultsForCategory(category);
    if (!defaults || !defaults[key]) return false;

    return currentValue === defaults[key].value;
}

/**
 * Get all defaults for a category
 * @param {string} category - Category name
 * @returns {object|null} - The defaults object or null
 */
export function getDefaultsForCategory(category) {
    const categoryMap = {
        financial: FINANCIAL_DEFAULTS,
        equipment: EQUIPMENT_COST_DEFAULTS,
        softCosts: SOFT_COST_DEFAULTS,
        tariff: TARIFF_DEFAULTS,
        loadModel: LOAD_MODEL_DEFAULTS,
        resilience: RESILIENCE_DEFAULTS,
        technical: TECHNICAL_DEFAULTS,
        generator: GENERATOR_DEFAULTS,
    };
    return categoryMap[category] || null;
}

/**
 * Get a specific default value
 * @param {string} category - Category name
 * @param {string} key - Key within the category
 * @returns {any} - The default value or undefined
 */
export function getDefaultValue(category, key) {
    const defaults = getDefaultsForCategory(category);
    if (!defaults || !defaults[key]) return undefined;
    return defaults[key].value;
}

/**
 * Get default metadata (source, description)
 * @param {string} category - Category name
 * @param {string} key - Key within the category
 * @returns {object|null} - The full default object with metadata
 */
export function getDefaultMetadata(category, key) {
    const defaults = getDefaultsForCategory(category);
    if (!defaults || !defaults[key]) return null;
    return defaults[key];
}

/**
 * Check if any values in an object are using defaults
 * Returns an array of keys that are using default values
 * @param {string} category - Category name
 * @param {object} values - Object with current values
 * @returns {string[]} - Array of keys using defaults
 */
export function findDefaultsInUse(category, values) {
    const defaults = getDefaultsForCategory(category);
    if (!defaults) return [];

    const defaultsInUse = [];
    for (const key of Object.keys(values)) {
        if (defaults[key] && values[key] === defaults[key].value) {
            defaultsInUse.push(key);
        }
    }
    return defaultsInUse;
}

/**
 * Generate a warning message for defaults in use
 * @param {string} category - Category name
 * @param {string[]} keysInUse - Array of keys using defaults
 * @returns {string} - Warning message
 */
export function generateDefaultsWarning(category, keysInUse) {
    if (!keysInUse || keysInUse.length === 0) return '';

    const defaults = getDefaultsForCategory(category);
    if (!defaults) return '';

    const items = keysInUse.map(key => {
        const def = defaults[key];
        if (!def) return null;
        return `${def.description}: ${def.value}${def.unit ? ' ' + def.unit : ''} (${def.source})`;
    }).filter(Boolean);

    if (items.length === 0) return '';

    return `Using default values for: ${items.join('; ')}`;
}

// =============================================================================
// LEGACY COMPATIBILITY EXPORTS
// These maintain backward compatibility with existing code
// TODO: Gradually migrate all usages to the new structure
// =============================================================================

export const DEFAULT_PV_COST_PER_KW = EQUIPMENT_COST_DEFAULTS.pvTurnkey.value;
export const DEFAULT_BATTERY_COST_PER_KWH = EQUIPMENT_COST_DEFAULTS.batteryEquipment.value;
export const DEFAULT_GENERATOR_COST_PER_KW = GENERATOR_DEFAULTS.gensetSizeKw.value;
export const DEFAULT_ANNUAL_KWH = 200000; // Deprecated - should require user input

export const DEFAULT_FINANCIAL = {
    elec_cost_escalation_rate_fraction: FINANCIAL_DEFAULTS.utilityEscalation.value,
    offtaker_discount_rate_fraction: FINANCIAL_DEFAULTS.discountRate.value,
    analysis_years: FINANCIAL_DEFAULTS.analysisYears.value,
    offtaker_tax_rate_fraction: FINANCIAL_DEFAULTS.taxRate.value,
    om_cost_escalation_rate_fraction: FINANCIAL_DEFAULTS.omEscalation.value,
};

// Fallback rates structure for useCapexRates.js compatibility
export const FALLBACK_RATES = {
    solar: {
        pv_turnkey: {
            rate: EQUIPMENT_COST_DEFAULTS.pvTurnkey.value,
            unit: EQUIPMENT_COST_DEFAULTS.pvTurnkey.unit
        }
    },
    equipment: {
        battery_equipment: {
            rate: EQUIPMENT_COST_DEFAULTS.batteryEquipment.value,
            unit: EQUIPMENT_COST_DEFAULTS.batteryEquipment.unit
        },
        battery_power: {
            rate: EQUIPMENT_COST_DEFAULTS.batteryPower.value,
            unit: EQUIPMENT_COST_DEFAULTS.batteryPower.unit
        },
        switchgear: {
            rate: EQUIPMENT_COST_DEFAULTS.switchgear.value,
            unit: EQUIPMENT_COST_DEFAULTS.switchgear.unit
        },
        generator: {
            rate: EQUIPMENT_COST_DEFAULTS.generatorEquipment.value,
            unit: EQUIPMENT_COST_DEFAULTS.generatorEquipment.unit
        }
    },
    installation: {
        battery_install: {
            rate: EQUIPMENT_COST_DEFAULTS.batteryInstall.value,
            unit: EQUIPMENT_COST_DEFAULTS.batteryInstall.unit
        },
        generator_install: {
            rate: EQUIPMENT_COST_DEFAULTS.generatorInstall.value,
            unit: EQUIPMENT_COST_DEFAULTS.generatorInstall.unit
        },
        electrical: { rate: 300, unit: '$/kW' }  // Kept for backwards compat
    },
    soft_costs: {
        engineering: {
            rate: SOFT_COST_DEFAULTS.engineering.value,
            unit: SOFT_COST_DEFAULTS.engineering.unit
        },
        permits: {
            rate: SOFT_COST_DEFAULTS.permits.value,
            unit: SOFT_COST_DEFAULTS.permits.unit
        },
        project_management: {
            rate: SOFT_COST_DEFAULTS.projectManagement.value,
            unit: SOFT_COST_DEFAULTS.projectManagement.unit
        },
        insurance: {
            rate: SOFT_COST_DEFAULTS.insurance.value,
            unit: SOFT_COST_DEFAULTS.insurance.unit
        },
        contingency: {
            rate: SOFT_COST_DEFAULTS.contingency.value,
            unit: SOFT_COST_DEFAULTS.contingency.unit
        },
        profit: {
            rate: SOFT_COST_DEFAULTS.profit.value,
            unit: SOFT_COST_DEFAULTS.profit.unit
        }
    }
};
